import { FormattedMessage } from 'react-intl';

import { Typography, TypographyProps } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import {
  canReserveShares,
  Constants,
  getAvailableOfferingFunds,
  getPercentFunded,
  isOfferingSoldOut,
} from '@arrived/common';
import { Offering, OfferingStatus } from '@arrived/models';
import { useCanUserTransactEarlyAccess } from '@arrived/queries';

import { Money } from '../../Money';
import { Numeral } from '../../Numeral';
interface PropertyMonetaryAvailabilityInfoProps {
  className?: string;
  dataE2e?: string;
  isAvailableInvested?: boolean;
  isStacked?: boolean;
  monetaryTypography?: TypographyProps<any>;
  offering: Offering;
  textTypography?: TypographyProps<any>;
}

const StyledPropertyMonetaryAvailabilityInfo = styled.span<{ $isStacked?: boolean }>`
  &.property-monetary-availability-info-wrapper {
    .property-monetary-availability-info-money {
      ${(props) => (props.$isStacked ? `display: block;` : `display: inline;`)}
      color: ${({ theme }) => theme.palette.neutrals.gray};
    }
  }
`;

export const PropertyMonetaryAvailabilityInfo = (props: PropertyMonetaryAvailabilityInfoProps) => {
  const {
    className,
    dataE2e,
    isAvailableInvested,
    isStacked,
    offering,
    monetaryTypography = { variant: 'body1' },
    textTypography = {
      variant: 'body2.semibold',
    },
  } = props;
  const isOfferingFullBool = isOfferingSoldOut(offering);

  const canUserTransactEarlyAccess = useCanUserTransactEarlyAccess();

  // Controls a "notify me" button which launches an Intercom SMS playbook.
  // When the button is shown, the "$ available" becomes "$ coming soon" to accommodate the button change.
  const showNotifyMeButton =
    offering.status === OfferingStatus.COMING_SOON ||
    (offering.status === OfferingStatus.TRANSACT_EARLY_ACCESS && !canUserTransactEarlyAccess);

  const monetaryValue = isOfferingFullBool ? offering.targetRaiseAmount : getAvailableOfferingFunds(offering);
  const fundsValue = (
    <Typography
      variant="body1.semibold"
      color={({ palette }) => palette.neutrals.gray}
      className="property-monetary-availability-info-money"
      {...monetaryTypography}
    >
      <Money value={monetaryValue} />
    </Typography>
  );

  const renderMessage = () => {
    if (isAvailableInvested) {
      if (isOfferingFullBool) {
        return (
          <FormattedMessage
            id="property-monetary-availability-invested-label"
            defaultMessage="{investedFunds} invested"
            values={{
              investedFunds: fundsValue,
            }}
          />
        );
      } else if (showNotifyMeButton) {
        return (
          <FormattedMessage
            id="property-monetary-availability-available-label"
            defaultMessage="{availableFunds} coming soon"
            values={{
              availableFunds: fundsValue,
            }}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="property-monetary-availability-available-label"
            defaultMessage="{availableFunds} available"
            values={{
              availableFunds: fundsValue,
            }}
          />
        );
      }
    }

    const fundedPercentageNumber = (
      <span className="numeric-value">
        <Numeral
          value={getPercentFunded(offering)}
          type="percent"
          minimumFractionDigits={0}
          maximumFractionDigits={0}
        />
      </span>
    );

    return canReserveShares(offering) ? (
      <FormattedMessage
        id="property-monetary-availability-reserved-label"
        defaultMessage="{fundedPercentage} reserved"
        values={{
          fundedPercentage: fundedPercentageNumber,
        }}
      />
    ) : (
      <FormattedMessage
        id="property-monetary-availability-funded-label"
        defaultMessage="{fundedPercentage} shares purchased"
        values={{
          fundedPercentage: fundedPercentageNumber,
        }}
      />
    );
  };

  return (
    <StyledPropertyMonetaryAvailabilityInfo
      $isStacked={isStacked}
      className={clsx('property-monetary-availability-info-wrapper', className)}
    >
      <Typography
        color={({ palette }) => palette.neutrals.gray}
        textTransform="uppercase"
        className="property-monetary-availability-info-text"
        data-e2e={dataE2e}
        {...textTypography}
      >
        {renderMessage()}
      </Typography>
    </StyledPropertyMonetaryAvailabilityInfo>
  );
};
